/* eslint-disable @typescript-eslint/no-explicit-any */
import { showAlertDialog } from '@juvare/common-web-ui/DialogHelper';
import { getErrorMessage } from '@juvare/widget-ui/Utils';
import React from 'react';
/**
 * Builds the "Validate" configuration tab for a widget editor, allowing users to specify
 * various validation rules (e.g. Required, length, range, uniqueness, regex, custom function).
 *
 * @param {WidgetProps<any, any>} props - The widget props containing context for tab generation.
 * @param {object} [params] - Optional configuration for which validation options to show.
 * @param {boolean} [params.required] - Show "Required" validation.
 * @param {boolean} [params.min] - Show "Min" numeric validation.
 * @param {boolean} [params.max] - Show "Max" numeric validation.
 * @param {boolean} [params.minLength] - Show "Min Length" validation for strings.
 * @param {boolean} [params.maxLength] - Show "Max Length" validation for strings.
 * @param {boolean} [params.uniqueInList] - Show "Unique in List" validation.
 * @param {boolean} [params.maxFileSize] - Show "Max File Size" validation (in bytes).
 * @param {boolean} [params.regex] - Show "Regex Pattern" validation input.
 * @param {DefaultFormWidgetConfigTypes[]} [params.children] - Optional additional custom form config items to append.
 * @param {(data: any) => any} [params.onLoad] - Optional hook to transform data on load.
 * @param {(data: any) => any} [params.onSubmit] - Optional hook to transform data on submit.
 * @returns {EditorTab} The configured validation tab for the editor UI.
 */
export function buildValidateTab(props, params) {
    const children = [];
    if (params === null || params === void 0 ? void 0 : params.required) {
        children.push({
            type: 'form.panel',
            children: [
                {
                    type: 'form.checkbox',
                    key: 'validate.required',
                    label: 'Required',
                    style: {
                        width: 400,
                    },
                },
                {
                    type: 'form.text',
                    key: 'validate.requiredMessage',
                    label: 'Message',
                    emptyAsNull: true,
                },
            ],
            style: {
                layout: 'row',
                verticalAlign: 'center',
            },
        });
    }
    if (params === null || params === void 0 ? void 0 : params.uniqueInList) {
        children.push({
            type: 'form.panel',
            children: [
                {
                    type: 'form.checkbox',
                    key: 'validate.uniqueInList',
                    label: 'Unique In List',
                    style: {
                        width: 400,
                    },
                },
                {
                    type: 'form.text',
                    key: 'validate.uniqueInListMessage',
                    label: 'Message',
                    emptyAsNull: true,
                },
            ],
            style: {
                layout: 'row',
                verticalAlign: 'center',
            },
        });
    }
    if (params === null || params === void 0 ? void 0 : params.min) {
        children.push({
            type: 'form.panel',
            children: [
                {
                    type: 'form.number',
                    key: 'validate.min',
                    label: 'Min',
                    style: {
                        width: 400,
                    },
                },
                {
                    type: 'form.text',
                    key: 'validate.minMessage',
                    label: 'Message',
                    emptyAsNull: true,
                },
            ],
            style: {
                layout: 'row',
                verticalAlign: 'center',
            },
        });
    }
    if (params === null || params === void 0 ? void 0 : params.max) {
        children.push({
            type: 'form.panel',
            children: [
                {
                    type: 'form.number',
                    key: 'validate.max',
                    label: 'Max',
                    style: {
                        width: 400,
                    },
                },
                {
                    type: 'form.text',
                    key: 'validate.maxMessage',
                    label: 'Message',
                    emptyAsNull: true,
                },
            ],
            style: {
                layout: 'row',
                verticalAlign: 'center',
            },
        });
    }
    if (params === null || params === void 0 ? void 0 : params.minLength) {
        children.push({
            type: 'form.panel',
            children: [
                {
                    type: 'form.number',
                    key: 'validate.minLength',
                    label: 'Min Length',
                    style: {
                        width: 400,
                    },
                },
                {
                    type: 'form.text',
                    key: 'validate.minLengthMessage',
                    label: 'Message',
                    emptyAsNull: true,
                },
            ],
            style: {
                layout: 'row',
                verticalAlign: 'center',
            },
        });
    }
    if (params === null || params === void 0 ? void 0 : params.maxLength) {
        children.push({
            type: 'form.panel',
            children: [
                {
                    type: 'form.number',
                    key: 'validate.maxLength',
                    label: 'Max Length',
                    style: {
                        width: 400,
                    },
                },
                {
                    type: 'form.text',
                    key: 'validate.maxLengthMessage',
                    label: 'Message',
                    emptyAsNull: true,
                },
            ],
            style: {
                layout: 'row',
                verticalAlign: 'center',
            },
        });
    }
    if (params === null || params === void 0 ? void 0 : params.maxFileSize) {
        children.push({
            type: 'form.panel',
            children: [
                {
                    type: 'form.number',
                    key: 'validate.maxFileSize',
                    label: 'Maximum File Size to Upload in Bytes',
                    style: {
                        width: 400,
                    },
                },
                {
                    type: 'form.text',
                    key: 'validate.maxFileSizeMessage',
                    label: 'Message',
                    emptyAsNull: true,
                },
            ],
            style: {
                layout: 'row',
                verticalAlign: 'center',
            },
        });
    }
    if (params === null || params === void 0 ? void 0 : params.regex) {
        children.push({
            type: 'form.panel',
            children: [
                {
                    type: 'form.text',
                    key: 'validate.regex',
                    label: 'Regex Pattern',
                    validate: {
                        custom: (value) => {
                            if (value) {
                                try {
                                    new RegExp(value);
                                }
                                catch (error) {
                                    return getErrorMessage(error);
                                }
                            }
                            return undefined;
                        },
                    },
                    style: {
                        monospace: true,
                        width: 400,
                    },
                },
                {
                    type: 'form.text',
                    key: 'validate.regexMessage',
                    label: 'Message',
                    emptyAsNull: true,
                },
            ],
            style: {
                layout: 'row',
                verticalAlign: 'center',
            },
        });
    }
    if (params === null || params === void 0 ? void 0 : params.children) {
        children.push(...params.children);
    }
    children.push({
        type: 'form.text',
        key: 'validate.custom',
        label: 'Custom JavaScript Function',
        emptyAsNull: true,
        multiline: true,
        rows: 6,
        style: {
            monospace: true,
        },
        validate: {
            custom: (value) => {
                if (value) {
                    try {
                        Function('"use strict"; return (function(value, handler) { ' + value + '; })')();
                    }
                    catch (error) {
                        return getErrorMessage(error);
                    }
                }
                return undefined;
            },
        },
    }, {
        type: 'form.button',
        label: 'Show Example',
        event: {
            onClick: () => {
                showAlertDialog({
                    title: 'Custom Validation Function Example',
                    content: React.createElement("pre", { style: { margin: 0 } }, exampleValidateCustomJavaScript),
                });
            },
        },
        style: {
            align: 'right',
        },
    });
    const onLoad = (data) => {
        var _a;
        // handle deprecation begin
        if (((_a = data.validate) === null || _a === void 0 ? void 0 : _a.uniqueInGrid) != null) {
            data.validate.uniqueInList = data.validate.uniqueInGrid;
            delete data.validate.uniqueInGrid;
        }
        // handle deprecation end
        if (params === null || params === void 0 ? void 0 : params.onLoad) {
            data = params.onLoad(data);
        }
        return data;
    };
    const onSubmit = (data) => {
        if (params === null || params === void 0 ? void 0 : params.onSubmit) {
            data = params.onSubmit(data);
        }
        return data;
    };
    return { label: 'Validate', children, onLoad, onSubmit };
}
const exampleValidateCustomJavaScript = `// This code validates that the number is even or odd based on other component
const evenOrOdd = handler.up().down('evenOrOdd').getData();
if (evenOrOdd === 'even') {
    return value % 2 !== 0
        ? 'Number must be even, for example: 2, 4, 6'
        : undefined;
} else {
    return value % 2 !== 1
        ? 'Number must be odd, for example: 1, 3, 5'
        : undefined;
}
`;
